import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './button';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200, 500, 1000];

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  itemLabel?: string; // e.g. "deliveries", "NCRs", "lines"
}

export function Pagination({
  currentPage,
  totalPages,
  totalCount,
  itemsPerPage,
  onPageChange,
  onPageSizeChange,
  itemLabel = 'items',
}: PaginationProps) {
  // Null safety - ensure values are defined
  const safeCurrentPage = currentPage || 1;
  const safeTotalPages = totalPages || 1;
  const safeTotalCount = totalCount || 0;
  const safeItemsPerPage = itemsPerPage || 10;

  if (safeTotalPages <= 1) return null;

  const startItem = (safeCurrentPage - 1) * safeItemsPerPage + 1;
  const endItem = Math.min(safeCurrentPage * safeItemsPerPage, safeTotalCount);

  return (
    <div className="flex items-center justify-between border-t pt-4 mt-4">
      <div className="flex items-center gap-3 text-sm text-foreground">
        {onPageSizeChange && (
          <div className="flex items-center gap-1">
            <span className="text-xs">Rows:</span>
            <select
              value={safeItemsPerPage}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="h-7 text-xs border rounded px-1 bg-background"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
        )}
        <span>
          Page {safeCurrentPage} of {safeTotalPages} • {safeTotalCount.toLocaleString()} total {itemLabel}
          <span className="ml-2 text-xs">
            (Showing {startItem.toLocaleString()}-{endItem.toLocaleString()})
          </span>
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(safeCurrentPage - 1)}
          disabled={safeCurrentPage === 1}
        >
          <ChevronLeft className="h-4 w-4" />
          Previous
        </Button>
        <div className="flex items-center gap-1">
          {Array.from({ length: Math.min(5, safeTotalPages) }, (_, i) => {
            let pageNum: number;
            if (safeTotalPages <= 5) {
              pageNum = i + 1;
            } else if (safeCurrentPage <= 3) {
              pageNum = i + 1;
            } else if (safeCurrentPage >= safeTotalPages - 2) {
              pageNum = safeTotalPages - 4 + i;
            } else {
              pageNum = safeCurrentPage - 2 + i;
            }
            return (
              <Button
                key={pageNum}
                variant={safeCurrentPage === pageNum ? 'default' : 'outline'}
                size="sm"
                onClick={() => onPageChange(pageNum)}
                className="w-8 h-8 p-0"
              >
                {pageNum}
              </Button>
            );
          })}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(safeCurrentPage + 1)}
          disabled={safeCurrentPage === safeTotalPages}
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
